import axios from 'axios';
import {
  AgencyInfo,
  CurrencyBanner,
  GetUserLastSearchResponse,
  SaveUserConfigResponse,
} from 'ts/shared/interfaces/User';
import { BranchB2CModel, UserB2CModel } from 'ts/shared/classes/UserB2CModel';

interface SubmitScreenshotsParams {
  screenshots: string[];
  orderId: number;
  lastScreenshot: string;
  transactionId?: string;
}
export const submitScreenshots = async (
  params: SubmitScreenshotsParams,
  url: string,
): Promise<any> => {
  try {
    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const configureAxiosBase = async (baseUrl: string) => {
  axios.defaults.baseURL = baseUrl;
  axios.defaults.headers.common['Content-Type'] = 'application/json';
  axios.defaults.headers.common.Accept = 'application/json';
};

export const saveUserConfig = async (config: any): Promise<SaveUserConfigResponse | undefined> => {
  try {
    const configStr = {
      ...config,
      input: {
        base: JSON.stringify(config.input.base),
        hotel: JSON.stringify(config.input.hotel),
      },
    };

    const url = 'Account/SaveUserConfig';
    return (await axios.post(url, configStr)).data;
  } catch (err) {
    return undefined;
  }
};

export const getUserConfig = async (defaultUserSiteConfig: any): Promise<any> => {
  try {
    const url = 'Account/GetUserConfig';
    const { output } = (await axios.get(url)).data;

    let baseConfig = defaultUserSiteConfig.base;
    if (output?.base) {
      const baseJson = JSON.parse(output?.base);

      if (typeof baseJson === 'object') {
        baseConfig = baseJson;
      }
    }

    let hotelConfig = defaultUserSiteConfig.hotel;
    if (output?.hotel) {
      const hotelJson = JSON.parse(output.hotel);

      if (typeof hotelJson === 'object') {
        hotelConfig = hotelJson;
      }
    }

    return {
      base: baseConfig,
      hotel: hotelConfig,
    };
  } catch (err) {
    return undefined;
  }
};

export const getSerializeAndCompressJson = async (data: any): Promise<string | undefined> => {
  try {
    const url = 'Account/GetSerializeAndCompressJson';
    return (await axios.post(url, data)).data.encodedJson;
  } catch (err) {
    return undefined;
  }
};

export const getUserLastSearch = async (
  product: 'Hotels' | 'Flights' | 'Dynamic' | 'IL' | 'Transfers',
): Promise<GetUserLastSearchResponse | undefined> => {
  try {
    const url = 'Account/GetUserLastSearch';
    return (await axios.post(url, { product })).data;
  } catch (err) {
    return undefined;
  }
};

export const addLogs = async (message: string) => {
  try {
    const url = 'Utils/Log';
    return (await axios.post(url, { message })).data;
  } catch (err) {
    return undefined;
  }
};

export const cleanSessionStorage = () => {
  sessionStorage.removeItem('filterHotels');
  sessionStorage.removeItem('filterHotels2');
  sessionStorage.removeItem('screenshotsHotel');
  sessionStorage.removeItem('screenshotsHotel2');
  sessionStorage.removeItem('detailsHotel');
  sessionStorage.removeItem('detailsHotel2');
  sessionStorage.removeItem('hotelFromSearch');
  sessionStorage.removeItem('hotelFromSearch2');
  sessionStorage.removeItem('cached_hotel_details');
  sessionStorage.removeItem('cached_hotel_details2');
  sessionStorage.removeItem('displayCurrencyHotels');
  sessionStorage.removeItem('hotelSearchGA');
  sessionStorage.removeItem('hotelSearchGA2');
  sessionStorage.removeItem('hotelsSearchFilters');
  sessionStorage.removeItem('lasestSearchUrl');
  sessionStorage.removeItem('lasestSearchUrl2');
  sessionStorage.removeItem('latestHotelPath');
  sessionStorage.removeItem('latestHotelPath2');
  sessionStorage.removeItem('multiRoomsBTokens');
  sessionStorage.removeItem('multiRoomsBTokens2');
  sessionStorage.removeItem('searchTokenHotels');
  sessionStorage.removeItem('searchTokenHotels2');
  sessionStorage.removeItem('searchTokenRooms');
  sessionStorage.removeItem('searchTokenRooms2');
  localStorage.removeItem('roomsStringHotel');
  localStorage.removeItem('roomsStringHotel2');
};

export const getAgencyInfo = async (): Promise<AgencyInfo | undefined> => {
  try {
    const url = 'Account/GetAgencyInfo';
    return (await axios.get(url)).data;
  } catch (err) {
    return undefined;
  }
};

export const updateAgencyInfo = async (
  user: UserB2CModel,
  branch: BranchB2CModel,
): Promise<any | undefined> => {
  try {
    const tokenUrl = '/antiforgery/token';
    const response = (await axios.get(tokenUrl)).data;
    const headers = {
      'X-CSRF-TOKEN': response.token,
    };

    const userData = {
      user_f_name: user.firstName,
      user_l_name: user.lastName,
      user_email: user.email,
      work_tel_1: user.workTel,
      mobile_1: user.mobile,
    };

    const url = 'Account/UpdateAgencyInfo';
    return (await axios.post(url, { user: userData, branch }, { headers })).data;
  } catch (err) {
    return undefined;
  }
};

export const getCurrencyBanner = async (): Promise<CurrencyBanner[] | undefined> => {
  const cached = sessionStorage.getItem('currencyBanner');
  if (cached) {
    return JSON.parse(cached);
  }

  try {
    const url = 'Account/GetCurrencyBanner';
    const currencies = (await axios.get(url)).data.currencies;
    if (currencies) {
      sessionStorage.setItem('currencyBanner', JSON.stringify(currencies));
    }
    return currencies;
  } catch (err) {
    return undefined;
  }
};
